// TGCLOUD-1818 — the filter-chip predicates for Search Family Records.
//
// Pure. No React, no network, no MUI. Every operator is a function from a
// criterion to a row test, so the whole operator set is unit-testable without
// rendering anything.
//
// TWO DELIBERATE CHOICES, both of which remove a class of bug rather than
// guarding against it:
//
// 1. NO REGULAR EXPRESSIONS ANYWHERE. `contains` and `startsWith` use
//    String.prototype.includes / startsWith on lower-cased values. The
//    commissioning brief asked for regex escaping, unit-tested against `.*`,
//    `(a+)+$` and friends. Escaping is the fix when you must build a pattern;
//    not building one is better. A user typing `(a+)+$` here matches the
//    literal characters and cannot cause catastrophic backtracking, because
//    there is no backtracking engine in the path at all.
//
// 2. DATES ARE COMPARED AS 'YYYY-MM-DD' STRINGS, NEVER AS Date OBJECTS.
//    `new Date('1940-06-01')` parses as UTC midnight and every local getter on
//    it returns the previous day west of Greenwich — the root cause of
//    TGCLOUD-1631/1632/1633. ISO date strings sort lexicographically in
//    calendar order, so `a < b` on two 'YYYY-MM-DD' strings IS the date
//    comparison, correct at every offset, with no parsing step to get wrong.
//    The only place a Date appears is the picker's own output, and
//    `toStoredDate` converts that using LOCAL components (see CLAUDE.md).

import { toStoredDate, toLabel } from 'src/util/util';
import { PRECISION } from 'src/util/partialDate';
import { diagnosesMap } from 'src/components/questionnaire/Constants';
import { displayableGenotypes } from 'src/components/admin/appReducer';

/** Operator vocabulary. Mirrors backend/util/personFilterFields.js. */
export const OPERATORS = Object.freeze({
  IS: 'is',
  IS_ONE_OF: 'isOneOf',
  CONTAINS: 'contains',
  STARTS_WITH: 'startsWith',
  IN_YEAR: 'inYear',
  BEFORE: 'before',
  AFTER: 'after',
  BETWEEN: 'between',
  IS_EMPTY: 'isEmpty',
  IS_NOT_EMPTY: 'isNotEmpty'
});

/** Operators that take no value at all. */
export const VALUELESS_OPERATORS = Object.freeze([
  OPERATORS.IS_EMPTY,
  OPERATORS.IS_NOT_EMPTY
]);

/** Operators whose value is a pair (from, to). */
export const RANGE_OPERATORS = Object.freeze([OPERATORS.BETWEEN]);

export const isValueless = (operator) => VALUELESS_OPERATORS.includes(operator);
export const isRange = (operator) => RANGE_OPERATORS.includes(operator);

const norm = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();

const isBlank = (v) =>
  v === null ||
  v === undefined ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/**
 * Split an "is one of" value into terms.
 *
 * Data Explorer ships the same operator and NEVER splits — `handleValue` does
 * `String(val)` and the backend wraps it as `{$in: ['Smith,Jones']}`, so it
 * matches nothing (TGCLOUD-1818 audit). Splitting here is the whole reason the
 * operator works. Quoted terms are honoured so a value containing a comma is
 * still expressible.
 *
 * @param {string} raw
 * @returns {string[]} trimmed, non-empty terms
 */
export const splitTerms = (raw) => {
  const text = String(raw ?? '');
  const out = [];
  let buf = '';
  let quoted = false;

  // Scanned rather than regexed. The regex this replaced tried its quoted
  // alternative at each start position, so after "a, " the cursor sat on the
  // SPACE, the quoted branch could not match there, and the greedy [^,]+ branch
  // swallowed the opening quote: 'Jones, "Smith, Jr"' came back as
  // ['Jones', '"Smith', 'Jr"'] — a quoted value was only ever honoured in
  // first position. A scanner has no positional blind spot.
  const flush = () => {
    const term = buf.trim();
    if (term.length) out.push(term);
    buf = '';
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      quoted = !quoted;
      continue;
    }
    if (ch === ',' && !quoted) {
      flush();
      continue;
    }
    buf += ch;
  }
  flush();
  return out;
};

// --- value extraction -------------------------------------------------------

// The SAME resolution the grid cells use, deliberately.
//
// A diagnosis is stored as a code ('breast-cancer') and displayed through
// `diagnosesMap` as "Breast Cancer" (UsersTableInfo's Disease column does
// exactly this). Matching the raw code meant typing what was on screen
// returned nothing — the single most confusing way for a filter to fail,
// because the evidence that it should have matched is visible in the row.
// Both forms are searchable: a user may reasonably type either the label or
// the code, and the cell shows both for phenotypes and genotypes.
const LIST_LABEL = (entry, map) => {
  if (entry === null || entry === undefined) return '';
  const label = toLabel(entry, map);
  if (label) return String(label);
  if (typeof entry === 'string') return entry;
  // personTags are { tag, tagColor } and toLabel does not know about them.
  return String(entry.tag ?? entry.name ?? entry.label ?? entry.value ?? '');
};

// Which display map, if any, a list field resolves its codes through.
const LIST_MAPS = { diagnoses: diagnosesMap };

/**
 * The searchable strings for a list-valued column.
 *
 * @param {*} raw the row's value for a list field
 * @param {string} [fieldKey] selects the display map and any display filtering
 * @returns {string[]}
 */
export const listTerms = (raw, fieldKey) => {
  // Genotypes carry a carrier-only sentinel that every display path strips
  // first (`displayableGenotypes`, whose own docstring says to drop it from
  // anything bound for display or search text). Matching it produced rows
  // whose Genotypes cell was visibly blank.
  const list = fieldKey === 'genotypes' ? displayableGenotypes(raw) : raw;
  if (!Array.isArray(list)) return [];
  const map = LIST_MAPS[fieldKey];
  const out = [];
  list.forEach((entry) => {
    const label = LIST_LABEL(entry, map);
    if (label) out.push(label);
    // The raw code too: a coded value ('HP:0001252', 'breast-cancer') is a
    // legitimate thing to search for, and the cell shows it for phenotypes.
    const code =
      typeof entry === 'string'
        ? entry
        : entry && typeof entry === 'object'
        ? entry.id
        : '';
    if (code && String(code) !== label) out.push(String(code));
  });
  return out;
};

/**
 * Read a field off a row, as the filter sees it.
 *
 * @param {object} row
 * @param {{key: string, type: string}} field
 * @returns {*} string | number | string[] | null
 */
export const readValue = (row, field) => {
  if (!row || !field) return null;
  const raw = row[field.key];
  if (field.type === 'list') return listTerms(raw, field.key);
  return raw;
};

// --- partial dates ----------------------------------------------------------

/**
 * Last calendar day of a month, without touching local time.
 *
 * Day 0 of month N+1 is the last day of month N. Built with Date.UTC and read
 * with getUTCDate, so it is pure arithmetic on a fixed frame — this is the one
 * safe use of Date in the file, because no local getter ever sees it.
 *
 * @param {number} year
 * @param {number} month 1-based
 * @returns {number}
 */
const lastDayOfMonth = (year, month) =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * The calendar interval a stored date actually denotes.
 *
 * THIS IS THE PARTIAL-DATE RULE, and it is written down before it is used
 * because the brief required that.
 *
 * A year-only date of birth is NOT stored as '1915'. It is stored as
 * '1915-01-01' with `dobPrecision: 'year'` — partialDate.js pads the missing
 * components, deliberately, to stay byte-compatible with the desktop XML
 * export. So the stored string says 1 January and the record means "some day
 * in 1915". Treating it as 1 January is how a record that means a whole year
 * gets excluded from a filter that covers most of it.
 *
 * So every stored date becomes an INTERVAL:
 *
 *   precision 'full'   '1965-03-12'  ->  ['1965-03-12', '1965-03-12']
 *   precision 'month'  '1980-06-01'  ->  ['1980-06-01', '1980-06-30']
 *   precision 'year'   '1915-01-01'  ->  ['1915-01-01', '1915-12-31']
 *
 * and a filter matches if ANY interpretation in that interval satisfies it.
 * Against a record storing only "1940", *before 1 June 1940* therefore MATCHES,
 * because January to May 1940 are before 1 June and they are legitimate
 * readings of the record.
 *
 * That direction is chosen on clinical grounds, not mathematical ones: in a
 * family-history search a false positive costs the user a glance, and a false
 * negative loses a relative who should have been found. Given the choice, be
 * over-inclusive.
 *
 * `estimatedDob` is the legacy fallback, read only when precision is absent —
 * partialDate.js treats a true `estimated` flag with no precision as year
 * precision, and so must this, or old records silently become exact days.
 *
 * @param {string} stored 'YYYY-MM-DD'
 * @param {string|undefined} precision  'full' | 'month' | 'year'
 * @param {boolean|undefined} estimated legacy flag
 * @returns {{lo: string, hi: string}|null} null when unparseable
 */
export const dateInterval = (stored, precision, estimated) => {
  const text = String(stored ?? '').trim();
  // Accepts every shape this product actually stores. A full date is the
  // common case, but an import preserves a reduced-precision source verbatim:
  // backend/util/normalizeImportDate.js matches /^\d{4}(-\d{1,2}(-\d{1,2})?)?$/
  // and returns it unchanged, precisely so a year-only or month-only record
  // keeps its precision. Requiring YYYY-MM-DD here silently dropped every one
  // of those people from every date filter.
  const match = /^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?/.exec(text);
  if (!match) return null;

  const [, y, m, d] = match;

  // The value cannot be more precise than its own shape: '1985' denotes a year
  // whatever dobPrecision claims. So take the LESS precise of the two.
  const shape = d ? PRECISION.FULL : m ? PRECISION.MONTH : PRECISION.YEAR;
  const declared = precision || (estimated ? PRECISION.YEAR : PRECISION.FULL);
  const rank = {
    [PRECISION.YEAR]: 0,
    [PRECISION.MONTH]: 1,
    [PRECISION.FULL]: 2
  };
  const effective = rank[declared] < rank[shape] ? declared : shape;

  if (effective === PRECISION.YEAR) {
    return { lo: `${y}-01-01`, hi: `${y}-12-31` };
  }

  const mm = String(Number(m)).padStart(2, '0');
  if (effective === PRECISION.MONTH) {
    const last = lastDayOfMonth(Number(y), Number(m));
    return {
      lo: `${y}-${mm}-01`,
      hi: `${y}-${mm}-${String(last).padStart(2, '0')}`
    };
  }

  const dd = String(Number(d)).padStart(2, '0');
  return { lo: `${y}-${mm}-${dd}`, hi: `${y}-${mm}-${dd}` };
};

/**
 * Which precision fields accompany a date column.
 * dob carries dobPrecision/estimatedDob; dod carries dodPrecision/estimatedDod.
 */
const precisionKeys = (fieldKey) => ({
  precision: `${fieldKey}Precision`,
  estimated: `estimated${fieldKey.charAt(0).toUpperCase()}${fieldKey.slice(1)}`
});

const rowInterval = (row, field) => {
  const { precision, estimated } = precisionKeys(field.key);
  return dateInterval(row?.[field.key], row?.[precision], row?.[estimated]);
};

/**
 * Normalise a filter's date value to 'YYYY-MM-DD'.
 *
 * Accepts what the DatePicker hands back (a local Date) and what the URL hands
 * back (already a string). `toStoredDate` is the repo's writer for a
 * `type: String` date column and reads a Date by its LOCAL components, which is
 * the correct half of the two mirror-image traps in CLAUDE.md — the picker
 * produces a local-anchored Date, so reading it in UTC would move the day west
 * of Greenwich.
 *
 * @param {Date|string|null|undefined} value
 * @returns {string} 'YYYY-MM-DD', or '' when absent/unusable
 */
export const toFilterDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    return m ? m[1] : '';
  }
  const stored = toStoredDate(value);
  return typeof stored === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(stored)
    ? stored
    : '';
};

// --- predicates -------------------------------------------------------------

const textPredicate = (operator, value) => {
  switch (operator) {
    case OPERATORS.IS: {
      const needle = norm(value);
      return (v) => norm(v) === needle;
    }
    case OPERATORS.CONTAINS: {
      const needle = norm(value);
      // Literal substring test. No pattern is ever compiled — see the header.
      return (v) => norm(v).includes(needle);
    }
    case OPERATORS.STARTS_WITH: {
      const needle = norm(value);
      return (v) => norm(v).startsWith(needle);
    }
    case OPERATORS.IS_ONE_OF: {
      const terms = splitTerms(value).map(norm);
      return (v) => terms.includes(norm(v));
    }
    default:
      return null;
  }
};

const listPredicate = (operator, value) => {
  const inner = textPredicate(operator, value);
  if (!inner) return null;
  // "any entry matches" — the only reading of contains/is that makes sense for
  // a list-valued cell.
  return (terms) => (Array.isArray(terms) ? terms.some(inner) : false);
};

const datePredicate = (operator, value) => {
  switch (operator) {
    case OPERATORS.IN_YEAR: {
      const year = String(value ?? '').trim();
      if (!/^\d{4}$/.test(year)) return () => false;
      // Precision-independent by construction: the interval never spans a year
      // boundary for any precision we store, so testing lo's year is enough.
      return (interval) =>
        interval !== null && interval.lo.slice(0, 4) === year;
    }
    case OPERATORS.BEFORE: {
      const bound = toFilterDate(value);
      if (!bound) return () => false;
      // ANY interpretation before the bound -> the earliest one decides.
      return (interval) => interval !== null && interval.lo < bound;
    }
    case OPERATORS.AFTER: {
      const bound = toFilterDate(value);
      if (!bound) return () => false;
      // ANY interpretation after the bound -> the latest one decides.
      return (interval) => interval !== null && interval.hi > bound;
    }
    case OPERATORS.BETWEEN: {
      const from = toFilterDate(value?.from);
      const to = toFilterDate(value?.to);
      if (!from || !to) return () => false;
      // Two intervals overlap iff each starts before the other ends. That IS
      // "any interpretation falls in range".
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      return (interval) =>
        interval !== null && interval.lo <= hi && interval.hi >= lo;
    }
    default:
      return null;
  }
};

/**
 * Build a row test for one criterion.
 *
 * Returns null when the criterion is not usable yet (no value on an operator
 * that needs one). A null predicate is IGNORED by applyCriteria rather than
 * matching nothing — a half-typed chip should not empty the grid.
 *
 * @param {{field: string, operator: string, value: *}} criterion
 * @param {{key: string, type: string}} field  the descriptor for criterion.field
 * @returns {((row: object) => boolean)|null}
 */
export const buildPredicate = (criterion, field) => {
  if (!criterion || !field) return null;
  const { operator, value } = criterion;

  if (operator === OPERATORS.IS_EMPTY) {
    return (row) => isBlank(readValue(row, field));
  }
  if (operator === OPERATORS.IS_NOT_EMPTY) {
    return (row) => !isBlank(readValue(row, field));
  }

  if (isRange(operator)) {
    // BOTH ends, not either. A half-filled range used to pass this guard and
    // then hit `() => false` in datePredicate, so a truncated link emptied the
    // grid instead of being ignored — the opposite of the half-typed rule two
    // lines down, and the wrong direction to fail.
    if (!value || !value.from || !value.to) return null;
  } else if (value === null || value === undefined || value === '') {
    return null;
  }

  if (field.type === 'date') {
    const test = datePredicate(operator, value);
    if (!test) return null;
    return (row) => test(rowInterval(row, field));
  }

  if (field.type === 'list') {
    const test = listPredicate(operator, value);
    if (!test) return null;
    return (row) => test(readValue(row, field));
  }

  const test = textPredicate(operator, value);
  if (!test) return null;
  return (row) => test(readValue(row, field));
};

/**
 * Apply every criterion to every row.
 *
 * ANDed, always. There is no logic operator and no grouping — see the brief.
 * `every` is what makes that true; an accidental `some` here would silently
 * widen every search, which is the wrong direction to fail on patient data.
 *
 * @param {object[]} rows
 * @param {Array<{field:string,operator:string,value:*}>} criteria
 * @param {Array<{key:string,type:string}>} fields the served descriptors
 * @returns {object[]} rows satisfying ALL criteria
 */
export const applyCriteria = (rows, criteria, fields) => {
  if (!Array.isArray(rows) || !rows.length) return rows || [];
  if (!Array.isArray(criteria) || !criteria.length) return rows;

  const byKey = new Map((fields || []).map((f) => [f.key, f]));
  const tests = criteria
    .map((c) => buildPredicate(c, byKey.get(c?.field)))
    .filter(Boolean);

  if (!tests.length) return rows;
  return rows.filter((row) => tests.every((test) => test(row)));
};
